/**
 * QUIPWA — KvStore
 *
 * JSON values by key, in the device-local database. Used for sync bookmarks
 * and other per-device facts that must survive reloads but never replicate.
 *
 * ```ts
 * await kv.set(KV.stateBookmark('appdata'), { fileId, version })
 * const bm = await kv.get<StateBookmark>(KV.stateBookmark('appdata'))
 * ```
 */
import type { IDBAdapter } from '../db/db-adapter.ts'

/** Every key lives here, so they are discoverable and never collide. */
export const KV = {
  stateBookmark: (location: string) => `sync.state.${location}`,
  stateDirty: 'sync.state.dirty',
  lastSyncedAt: 'sync.lastSyncedAt',
  ownerEmail: 'account.ownerEmail',
  idPool: 'drive.idPool',
  /** Drive `version` of a deck's .wflash whose content this device holds. */
  deckVersion: (deckInstanceId: string) => `sync.deck.${deckInstanceId}`,
} as const

export class KvStore {
  private readonly db: IDBAdapter

  constructor(db: IDBAdapter) {
    this.db = db
  }

  async get<T>(key: string): Promise<T | null> {
    const row = await this.db.queryOne<{ value: string }>('SELECT value FROM kv WHERE key = ?', [key])
    if (!row) return null
    try {
      return JSON.parse(row.value) as T
    } catch {
      return null
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    await this.db.run(
      `INSERT INTO kv (key, value, updated_at) VALUES (?, ?, ?)
       ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
      [key, JSON.stringify(value), Date.now()]
    )
  }

  async delete(key: string): Promise<void> {
    await this.db.run('DELETE FROM kv WHERE key = ?', [key])
  }

  /** Remove every key starting with `prefix` (e.g. all sync bookmarks on account switch). */
  async deletePrefix(prefix: string): Promise<void> {
    await this.db.run("DELETE FROM kv WHERE key LIKE ? ESCAPE '\\'", [`${prefix.replace(/[\\%_]/g, '\\$&')}%`])
  }
}
